import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, RefreshCw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { crmApi } from "@/lib/ownerClient";

export default function AuditView() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ action_prefix: "", actor_type: "" });

  const reload = async () => {
    setLoading(true);
    try {
      const res = await crmApi.audit({ ...filter, limit: 200 });
      setEvents(res?.events || []);
    } catch (exc) {
      toast.error(exc.message || "Failed to load audit log");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await crmApi.audit({ limit: 200 });
        if (active) setEvents(res?.events || []);
      } catch (exc) {
        if (active) toast.error(exc.message || "Failed to load audit log");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  return (
    <div className="space-y-6" data-testid="owner-audit">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Compliance Audit Log</h1>
        <p className="text-sm text-zinc-400 mt-1">
          Append-only ledger of every binder upload, vault write, agent action, and Stripe touch.
          Used by Dana for compliance reporting.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-[220px]">
          <label className="text-xs text-zinc-400">Action prefix</label>
          <Input value={filter.action_prefix} onChange={(e) => setFilter({ ...filter, action_prefix: e.target.value })} placeholder="library.binder" data-testid="audit-filter-action" />
        </div>
        <div className="min-w-[160px]">
          <label className="text-xs text-zinc-400">Actor type</label>
          <Input value={filter.actor_type} onChange={(e) => setFilter({ ...filter, actor_type: e.target.value })} placeholder="user / agent / system" data-testid="audit-filter-actor" />
        </div>
        <Button onClick={reload} variant="outline" className="border-zinc-700 text-white" data-testid="audit-refresh">
          <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-400 flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : events.length === 0 ? (
        <Card className="bg-zinc-900/40 border-zinc-800"><CardContent className="py-10 text-center text-sm text-zinc-400"><ShieldCheck className="h-6 w-6 text-emerald-300 mx-auto mb-2" />No events match this filter.</CardContent></Card>
      ) : (
        <Card className="bg-zinc-900/40 border-zinc-800">
          <CardContent className="p-0">
            <ul className="divide-y divide-zinc-800">
              {events.map((e) => (
                <li key={e.id} className="p-3 flex items-start justify-between gap-3" data-testid={`audit-${e.id}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-sm text-white">{e.action}</span>
                      <Badge
                        variant="outline"
                        className={
                          e.outcome === "pass"
                            ? "border-emerald-500/40 text-emerald-300"
                            : e.outcome === "fail"
                              ? "border-rose-500/40 text-rose-300"
                              : "border-zinc-700 text-zinc-400"
                        }
                      >
                        {e.outcome}
                      </Badge>
                    </div>
                    <div className="text-[11px] text-zinc-500 mt-1">
                      {e.actor_type} · {e.actor_id} · {new Date(e.created_at).toLocaleString()}
                    </div>
                    {e.detail && Object.keys(e.detail).length > 0 && (
                      <pre className="text-[11px] text-zinc-400 mt-2 whitespace-pre-wrap break-words bg-black/40 rounded p-2 border border-zinc-900">{JSON.stringify(e.detail, null, 2)}</pre>
                    )}
                    {e.error_message && <div className="text-xs text-rose-300 mt-1">{e.error_message}</div>}
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
